/**
 * CreateMessageModal - Modal for creating new message threads
 */

import React, { useState, useEffect, useCallback, useContext, memo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { confirmDialog } from 'primereact/confirmdialog';

import CreateMessageForm from './CreateMessageForm';

import { useMessageContext } from '../../context';
import { useCreateMessage } from '../../hooks/useMessageMutations';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useFileUpload } from '../../hooks/useFileUpload';
import { getClientUserList } from '../../services/messageService';
import { UserDetailsContext } from 'context/userDetailsContext';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { generateFullName } from 'utils/generateFullNameUtils';
import { getTenantDetails } from 'utils/utils';
import { sortByKey } from 'utils/utils';
import useAutomatedTime from 'hooks/useAutomatedTime';
import useFetchFiles from 'hooks/useFetchFiles';

function CreateMessageModal({ show, setShow }) {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { addToast } = useToast();
  const userContext = useContext(UserDetailsContext);
  const [tenantId] = getTenantDetails();

  const { isFromLead, isFromCase, isGlobal, caseDetails, caseId, isFromClientPortal, getCaseDetails } = useMessageContext();

  // Client portal: the active case the client has selected in their session
  const clientPortalCase = isFromClientPortal ? userContext?.clientSelectedCase : null;

  const { onMountUnMount, onOperationBreak } = useAutomatedTime(caseId, caseDetails?.case_description?.case_name);

  const formMethods = useForm();
  const {
    resetField,
    setValue,
    getValues,
    watch,
    formState: { isDirty },
    reset,
  } = formMethods;

  const watchedValues = watch();

  const createMessageMutation = useCreateMessage();
  const { files, addFiles, removeFile, uploadFiles, uploadProgress } = useFileUpload();
  const { files: fetchedExistingFiles, getFiles: fetchExistingFiles } = useFetchFiles();

  const [attendeesList, setAttendeesList] = useState([]);
  const [scheduleData, setScheduleData] = useState(null);

  // Auto-save
  const { draftData, hasDraft, clearDraft, saveDraft } = useAutoSave({
    caseId: caseId || id,
    autoSaveType: 'thread',
    getFormValues: getValues,
    formValues: watchedValues,
    isDirty: isDirty || Boolean(scheduleData),
    enabled: true,
  });

  // Restore draft on mount
  useEffect(() => {
    if (draftData && Object.keys(draftData).length > 0) {
      const currentValues = getValues();
      reset({ ...currentValues, ...draftData }, { keepDirty: false });

      if (draftData?.schedule_data) setScheduleData(draftData.schedule_data);
      if (isGlobal && !caseId && draftData?.caseId?.id) loadCaseContacts(draftData.caseId.id);
    }
  }, [draftData]);

  // Set initial values
  useEffect(() => {
    if (caseId) {
      setValue('caseId', caseId);
      getCaseClients(caseId);
    } else if (clientPortalCase) {
      setValue('caseId', { id: clientPortalCase.case_id, case_name: clientPortalCase.case_name });
      getCaseClients(clientPortalCase.case_id);
    }
    !isFromClientPortal && typeof onMountUnMount === 'function' && onMountUnMount('create-message', 'mounted', caseId);
    return () => {
      !isFromClientPortal && typeof onOperationBreak === 'function' && onOperationBreak();
    };
  }, []);

  // ── Load contacts for selected case ───────────────────────────────────────

  const getCaseClients = useCallback(
    (selectedCaseId) => {
      resetField('messageTo');
      resetField('event_attachment');
      resetField('task_attachment');
      resetField('existing_files');
      if (selectedCaseId) {
        loadCaseContacts(selectedCaseId);
        fetchExistingFiles({ case_id: selectedCaseId, all_files: true });
      } else {
        loadCaseContacts();
      }
    },
    [isFromClientPortal, fetchExistingFiles]
  );

  const loadCaseContacts = useCallback(
    (idCase = '') => {
      getClientUserList({
        caseId: idCase,
        all_contacts: isFromClientPortal ? null : true,
        contact_id: isGlobal || isFromClientPortal ? userContext?.userDetails?.contact_id : false,
        is_team_member_list: isFromClientPortal ? null : true,
        is_include_groups: true,
        active_groups: true,
        excludeFromLoading: false,
      })
        .then((response) => {
          if (!response?.data?.length) return;

          const groups = [];
          const firmUsers = [];
          const contacts = [];

          response.data.forEach((el) => {
            if (el?.res_type === 'user-group') {
              groups.push({ label: el.group_name, value: el.user_group_id, ...el });
            } else {
              const label = el.contact_type === 'person' || el.first_name ? generateFullName(el) : el.title;
              const userData = {
                label,
                value: el.client_cognito_username || el.contact_id,
                email: el.email,
                is_create_client_portal: el.is_create_client_portal,
                access_level: el.access_level,
                ...el,
              };
              if (el?.contact_type) contacts.push(userData);
              else firmUsers.push(userData);
            }
          });

          const grouped = [];
          if (groups.length > 0)
            grouped.push({ label: 'Groups', items: groups.sort((a, b) => (a.group_name || '').localeCompare(b.group_name || '')) });
          if (firmUsers.length > 0)
            grouped.push({
              label: 'Firm Users',
              items: sortByKey(firmUsers, 'first_name').filter((u) => u.firm_user_id !== userContext?.userDetails?.firm_user_id),
            });
          if (contacts.length > 0) grouped.push({ label: 'Contacts', items: sortByKey(contacts, 'first_name') });
          grouped.reverse();
          setAttendeesList(grouped);
        })
        .catch(console.error);
    },
    [isGlobal, isFromClientPortal, userContext]
  );

  // ── Submit ────────────────────────────────────────────────────────────────

  const onSubmit = useCallback(
    async (data) => {
      // Determine case name
      const caseName = isFromLead || isFromCase ? caseDetails?.case_description?.case_name || 'TBD' : data?.caseId?.case_name;

      // Upload files
      const uploadedAttachments = await uploadFiles(data?.caseId?.id || caseId);
      if (uploadedAttachments === null) return;

      let allAttachments = uploadedAttachments || [];
      if (data.existing_files?.length > 0) allAttachments = [...allAttachments, ...data.existing_files];

      // Build recipients
      const usersArray = attendeesList?.filter((g) => g.label === 'Contacts' || g.label === 'Firm Users')?.flatMap((g) => g.items);
      const messageTo = (data.messageTo || []).flatMap((val) => {
        const full = usersArray?.find((u) => (u.client_cognito_username || u.contact_id) === val);
        if (!full) return [];
        return [
          {
            recipient_id: full.client_cognito_username ?? full.contact_id,
            client_cognito_username: full.client_cognito_username,
            firm_user_id: full.firm_user_id ?? '',
            contact_id: full.contact_id ?? '',
            access_level: full.access_level,
          },
        ];
      });

      const groupsArray = attendeesList?.find((g) => g.label === 'Groups')?.items;
      const groups = (data.messageTo || []).flatMap((val) => {
        const fullGroup = groupsArray?.find((g) => g.user_group_id === val);
        if (!fullGroup) return [];
        return [{ group_id: fullGroup.user_group_id, group_name: fullGroup.group_name, sk: fullGroup.sk }];
      });

      // Build payload using the new API schema
      const payload = {
        type: 'thread',
        tenant_id: tenantId,
        case_id: isGlobal || isFromClientPortal ? data?.caseId?.id : isFromLead ? caseId : id,
        subject: data.subject,
        message: data.message,
        message_to: messageTo,
        groups,
        attachment: allAttachments,
        event_attachment: (data.event_attachment || []).map(({ label: _l, value: _v, ...rawItem }) => rawItem),
        task_attachment: (data.task_attachment || []).map(({ label: _l, value: _v, ...rawItem }) => rawItem),
        scheduled_send_at: scheduleData?.datetime || '',
        resolution_status: data.resolution_status || false,
        is_lead: !!isFromLead,
        case_name: caseName || '',
      };

      try {
        await createMessageMutation.mutateAsync(payload);
        !isFromClientPortal && typeof onMountUnMount === 'function' && onMountUnMount('create-message', 'unmounted', payload?.case_id);
        clearDraft();
        addToast(
          false,
          toastConstant.toasterType.SUCCESS,
          toastConstant.api.SUCCESS,
          scheduleData ? 'Message scheduled successfully' : 'Message sent successfully'
        );
        handleClose();
      } catch (err) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, err?.message || 'Failed to send message');
      } finally {
        if (isFromCase || isFromLead) {
          getCaseDetails(caseId);
        }
      }
    },
    [
      createMessageMutation,
      uploadFiles,
      attendeesList,
      scheduleData,
      tenantId,
      caseId,
      id,
      isFromLead,
      isFromCase,
      isGlobal,
      isFromClientPortal,
      caseDetails,
    ]
  );

  const handleClose = useCallback(() => {
    if (document.activeElement?.blur) document.activeElement.blur();
    setShow(false);
    const params = new URLSearchParams(location.search);
    params.delete('createMessage');
    history.push({ pathname: location.pathname, search: params.toString() });
  }, [setShow, location, history]);

  const clearUnsavedData = useCallback(() => {
    if (isDirty || hasDraft || scheduleData) {
      confirmDialog({
        message: 'Do you want to draft save this message?',
        header: 'Discard',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          saveDraft({ ...getValues(), schedule_data: scheduleData });
          handleClose();
        },
        reject: () => {
          clearDraft();
          handleClose();
        },
      });
    } else {
      handleClose();
    }
  }, [isDirty, hasDraft, scheduleData]);

  const headerComponent = useCallback(() => {
    return (
      <div>
        <i
          className="icon-window-filled modal-close-icon F-size14 pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            clearUnsavedData();
          }}
        />
      </div>
    );
  }, [clearUnsavedData]);

  return (
    <Dialog header={headerComponent} visible={show} onHide={clearUnsavedData} className="create-msg-popup" closable={false} modal>
      <FormProvider {...formMethods}>
        <CreateMessageForm
          onSubmit={onSubmit}
          attendeesList={attendeesList}
          files={files}
          addFiles={addFiles}
          removeFile={removeFile}
          getCaseClients={getCaseClients}
          handleClose={clearUnsavedData}
          isSubmitting={createMessageMutation.isPending}
          uploadPercentage={uploadProgress}
          scheduleData={scheduleData}
          setScheduleData={setScheduleData}
          fetchedExistingFiles={fetchedExistingFiles}
        />
      </FormProvider>
    </Dialog>
  );
}

export default memo(CreateMessageModal);
